import _ from 'lodash';
import { HblPoNo } from '../../../../fms.core/HblPoNo';
import { OhContainer } from '../../../../fms.core/OhContainer';
import { OiHbl } from '../../../../fms.core/OiHbl';
import { formatAmount } from '../../../../util';
import { IApplicationContext } from '../../../../ApplicationContext';

interface PoCheckResultCoItem {
  mCntId: number,
  containerNo: string,
  valid: boolean,
  requiredAmount: number,
  notAppliedAmount: number,
  poElements: number,
};
interface PoCheckResult {
  valid: boolean,
  coItems: Array<PoCheckResultCoItem>,
  poItems: Array<HblPoNo>,
};

export function checkPoInfoLineItems(containerInfoLineItems:Array<OhContainer>, poInfoLineItems:Array<HblPoNo>):PoCheckResult {
  const co = containerInfoLineItems.filter(x => x.agentShow) || [];

  const result:PoCheckResult = {
    valid: true,
    coItems: co.map(x => ({
      mCntId: x.mCntId,
      containerNo: x.containerNo,
      valid: true,
      requiredAmount: x.pkgs || 0,
      notAppliedAmount: x.pkgs || 0,
      poElements: 0,
    })),
    poItems: poInfoLineItems,
  };

  for (let i = 0; i < result.poItems.length; ++i) {
    const po = result.poItems[i];
    if (!po.cntrLinkId) {
      // Container not selected for this line item
      continue;
    }
    const coIndex = result.coItems.findIndex(x => x.mCntId === po.cntrLinkId);
    if (coIndex < 0) {
      // Should never happen
      continue;
    }

    result.coItems[coIndex].notAppliedAmount = result.coItems[coIndex].notAppliedAmount - (po.shipQty ?? 0);
    result.coItems[coIndex].poElements = result.coItems[coIndex].poElements + 1;
  }

  for (let i = 0; i < result.coItems.length; ++i) {
    // If total sum of amounts entered is above total pkgs - it is not valid
    const totalsQty = poInfoLineItems.reduce((sum, it) => sum + (it.containerNo === result.coItems[i].containerNo ? it.shipQty : 0), 0);
    if ((result.coItems[i].requiredAmount !== totalsQty) && result.coItems[i].poElements > 0) {
      result.coItems[i].valid = false;
      result.valid = false;
    }
  }

  if (!result.valid) {
    // If not valid, for each container item find the first in po, apply whole amount to it and reset containers for others
    for (let i = 0; i < result.coItems.length; ++i) {
      if (result.coItems[i].valid) {
        continue;
      }

      if (result.coItems[i].notAppliedAmount > 0) {
        // Qty is missing. Apply it to the last matching item
        for (let j = result.poItems.length - 1; j >= 0; --j) {
          if (result.poItems[j].cntrLinkId === result.coItems[i].mCntId) {
            let curQty = result.poItems[j].shipQty || 0;
            result.poItems[j].shipQty = curQty + result.coItems[i].notAppliedAmount;
            break;
          }
        }
      } else {
        // There is extra qty applied. Start reducing from last item appropriately

        for (let j = result.poItems.length - 1; j >= 0; --j) {
          if (result.coItems[i].notAppliedAmount === 0) {
            break;
          }

          if (result.poItems[j].cntrLinkId === result.coItems[i].mCntId) {
            let curQty = result.poItems[j].shipQty || 0;
            const delta = Math.min(curQty, -result.coItems[i].notAppliedAmount);
            result.poItems[j].shipQty = curQty - delta;
            result.coItems[i].notAppliedAmount = result.coItems[i].notAppliedAmount + delta;
          }
        }
      }
    }
  }

  return result;
}


export function calculateBodyContent(current:Partial<OiHbl>, change:Partial<OiHbl>, isIntnl:boolean , containerSummary: string = 'XX'): Partial<OiHbl> {
  // const c = _.has(change, 'container') ? change.container : current.container;
  const pkg = _.has(change, 'pkgs') ? change.pkgs : current.pkgs;
  const packagesUnit = _.has(change, 'punit') ? change.punit : current.punit;
  const cbm = _.has(change, 'cbm') ? change.cbm : current.cbm;
  const cft = _.has(change, 'cft') ? change.cft : current.cft;
  const kgs = _.has(change, 'kgs') ? change.kgs : current.kgs;
  const lbs = _.has(change, 'lbs') ? change.lbs : current.lbs;

  // const noOfPackages = c?.map(x => ({ pkgs: x.pkgs, agentShow: x.agentShow }))
  //   .filter(x => !!x && !!x.pkgs && x.agentShow).reduce((acc, x) => acc + (x.pkgs ?? 0), 0);
  const numberOfPackages = `${formatAmount(pkg??0, 0)} ${packagesUnit}`;
  let tempMarkPkg='';
  if(current.lclFcl === 'F'){
    tempMarkPkg = numberOfPackages + '\r\n' + (containerSummary === 'XX' ? current.containerSummary : containerSummary); 
  }

  const grossWeight = `${formatAmount(kgs, 2)} KGS` + (isIntnl ? '' : `\r\n${formatAmount(lbs, 2)} LBS`);

  const measurement = `${formatAmount(cbm, 3)} CBM` + (isIntnl ? '' : `\r\n${formatAmount(cft, 0)} CFT`);

  return {
    markPkg: _.isEmpty(tempMarkPkg) ? numberOfPackages: tempMarkPkg,
    markGross: grossWeight,
    markMeasure: measurement,
  };
}

export function formatHblType(h:string|null) {
  if (h === 'S') {
    return 'Standard';

  } else if (h === 'C') {
    return 'Co-Load';
  }

  return 'None';
}

export function copyEntity(copyApiResult: OiHbl, newModel: OiHbl, appContext: IApplicationContext): Partial<OiHbl> {
  const userId = appContext.user?.isSuntekAdmin ? '' : appContext.user ? appContext.user.id??'' : '';     // User 계정
  
  const copiedHbl: Partial<OiHbl> = {
    ...copyApiResult,
    // mblId: undefined,
    //oimblId: newModel.oimblId,
    id: newModel.id,
    branch: appContext.systemSettings?.enableBranch ? (copyApiResult.branch ? copyApiResult.branch : (appContext.systemSettings.userBranch ?? '')) : '',
    shipmentType: copyApiResult.shipmentType ? copyApiResult.shipmentType : (appContext.systemSettings?.defaultShipmentImport ?? 'I'),
    moveType: copyApiResult.moveType ? copyApiResult.moveType : 'CY/CY',
    operator: copyApiResult.operator ? copyApiResult.operator : userId, 
    
    // lastFreeDay: newModel.lastFreeDay,
    // goDate: newModel.goDate,
    availDate: newModel.availDate,

    // fEta: newModel.fEta,
    doordlvEta: newModel.doordlvEta,
    foreignEta: newModel.foreignEta,

    // mblNo: newModel.mblNo,
    // smblNo: newModel.smblNo,
    // agentRefNo: newModel.agentRefNo,
    // undCode: newModel.undCode,
    // postDate: newModel.postDate,

    itDestination: newModel.itDestination,
    soNo: newModel.soNo,
    shipmentId: newModel.shipmentId,

    customerName: copyApiResult.isCreditHold ? newModel.customerName : copyApiResult.customerName,
    customer: copyApiResult.isCreditHold ? newModel.customer : copyApiResult.customer,
    //salesCostEntryView1.HblCustomerId:copyApiResult.result.isCreditHold ? salesCostEntryView1.HblCustomerId : newModel...,
    //salesCostEntryView1.HblCustomerName:copyApiResult.result.isCreditHold ? salesCostEntryView1.HblCustomerName : newModel...,

    completeBill: newModel.completeBill,
    completeInvo: newModel.completeInvo,
    completeCrdb: newModel.completeCrdb,
    fileClosed: newModel.fileClosed,
    closedBy: newModel.closedBy,
    closeDate: newModel.closeDate,
    confirmDate: newModel.confirmDate,
    confirmedBy: newModel.confirmedBy,
    selectContainer: true,
    exprls: newModel.exprls,
    oblcol: newModel.oblcol,
    podRcvd: newModel.podRcvd,
    mblLinkedCrDbCnt: newModel.mblLinkedCrDbCnt,

    actClosed: newModel.actClosed,
    actClosedBy: newModel.actClosedBy,
    actCloseDate: newModel.actCloseDate,
    accountClosed: newModel.accountClosed,
    accountClosedBy: newModel.accountClosedBy,
    accountCloseDate: newModel.accountCloseDate,

    //amsblNo: appContext.suntekSettings?.oceanAir?.doNotCopyAMSNo ? newModel.amsblNo : copyApiResult.result.amsblNo,
    amsblNo: newModel.amsblNo,

    pimsLinkDate: newModel.pimsLinkDate,
    pimsLinkId: newModel.pimsLinkId,
    pimsLinkUserId: newModel.pimsLinkUserId,
    pimsPoId: newModel.pimsPoId,

    nomi: appContext.suntekSettings?.company?.companyId.substring(0, 3) === 'RTW' ? 'C' : (appContext.suntekSettings?.oceanAir?.useFreeAsDefault??false) ? '' : 'N',
    u2Date: null,
    u1Date: null,
    u2Id: '',
    u1Id: '',
    statusBill: 'E',
    statusInvo: 'E',
    statusCrDb: 'E',
    // container: newModel.container,
    submblSurfix: newModel.submblSurfix,
    custRefNo: newModel.custRefNo,
    punit: 'CTNS',
    pkgs: newModel.pkgs,
    kgs: newModel.kgs,
    lbs: newModel.lbs,
    cbm: newModel.cbm,
    pMemo: newModel.pMemo,
    //lclFcl: newModel.lclFcl,
    // multinameId: newModel.multinameId,
    // doordlvLocation: newModel.doordlvLocation,
    isfNo: newModel.isfNo,
    sentTo: newModel.sentTo,
    truckLoad: newModel.truckLoad,
    customer2: newModel.customer2,
    customerName2: newModel.customerName2,
    palletQty: newModel.palletQty,
    // customStatus: newModel.customStatus,
    // ror: newModel.ror,
    // doorMove: newModel.doorMove,
    oblDate: newModel.oblDate,
    oblBy: newModel.oblBy,
    cHoldDate: newModel.cHoldDate,
    cHoldBy: newModel.cHoldBy,
    isRlsDate: newModel.isRlsDate,
    rlsDate: newModel.rlsDate,
    releaseBy: newModel.releaseBy,
    isClear: newModel.isClear,
    clearDate: newModel.clearDate,
    clearBy: newModel.clearBy,
    fdaHold: newModel.fdaHold,
    fdaHoldDate: newModel.fdaHoldDate,
    fdaHoldBy: newModel.fdaHoldBy,
    fdaRls: newModel.fdaRls,
    fdaRlsDate: newModel.fdaRlsDate,
    fdaRlsBy: newModel.fdaRlsBy,
    dutyAgent: newModel.dutyAgent,
    dutyAgentName: newModel.dutyAgentName,
    dutyCustomer: newModel.dutyCustomer,
    dutyCustomerName: newModel.dutyCustomerName,
    invoiceBill: {
      billLineItems: [],
      invoiceLineItems: [],
      crdbLineItems: [],
    },
    useAms: newModel.useAms,
    useIsf: newModel.useIsf,
    chbRspCode: '',
    chbRspMsg: '',

    agent2:newModel.agent2,
    agentName2:newModel.agentName2,
    agent3:newModel.agent3,
    agentName3:newModel.agentName3,
    other: [],

    // HBL Body & Entry Info
    // refNo: '',
    hblNo: newModel.hblNo,
    // mark: newModel.mark,
    // markPkg: newModel.markPkg,
    // markGross: newModel.markGross,
    // markMeasure: newModel.markMeasure,
    itClass: newModel.itClass,
    itNo: newModel.itNo,
    itDate: newModel.itDate,
    itPlace: newModel.itPlace,
    // itCarrier: newModel.itCarrier,
    // itCarrierName: newModel.itCarrierName,
    // bondNo: newModel.bondNo,
    // otherDescript: newModel.otherDescript,
    // truckloadNo: newModel.truckloadNo,
    // country: newModel.country,
    // loadingPort: newModel.loadingPort,
    // etd: newModel.etd,
    // discharge: newModel.discharge,
    // eta: newModel.eta,
    previtClass: newModel.previtClass,
    previtNo: newModel.previtNo,
    previtDate: newModel.previtDate,
    previtPort: newModel.previtPort,
    body5: copyApiResult.body5 ?? '',
    
    clearanceBy: newModel.clearanceBy,
    cartageBy: newModel.cartageBy,
    acquittalNo: newModel.acquittalNo,
    prevCargoNo: newModel.prevCargoNo,
    manifestFrom: newModel.manifestFrom,
    toa: newModel.toa,
    vehicleNo: newModel.vehicleNo,
  };

  return copiedHbl;
}

export function cleanUpOhContainer(tempOih: OiHbl) {
  const oih = _.cloneDeep(tempOih);

  // - Remove Duplicated MCntId, MCntId not in OmContainer
  const dynHcsWithDupMCntId = oih.container.reduce((result, x) => {
    const existing = result.find(r => r.MCntId === x.mCntId);
    if (existing) {
      existing.Count++;
    } else {
      result.push({ MCntId: x.mCntId, MaxHId: x.id, Count: 1 });
    }
    return result;
  }, [] as { MCntId: number; MaxHId: number; Count: number }[])
    .filter(r => r.Count > 1);

  dynHcsWithDupMCntId.forEach(obj => {
    const hcToRemove = oih.container?.find(c => c.id === obj.MaxHId);
    if (hcToRemove) {
      const index = oih.container?.indexOf(hcToRemove) ?? 0;
      oih.container?.splice(index, 1);
    }
  });

  // - Remove OhContainer which has MCntId not in OmContainers
  const hcsWithInvalidMCntId = oih.container.filter(x => !oih.omContainers?.some(m => m.id === x.mCntId));

  hcsWithInvalidMCntId.forEach(hc => {
    const index = oih.container?.indexOf(hc) ?? 0;
    oih.container?.splice(index, 1);
  });

  // Fill ohContainer property from OmContainer, if not exist in Container, add new OhContainer
  for (let i = 0; i < oih.omContainers.length; i++) {
    const omc = oih.omContainers[i];

    if (omc.containerNo) { // ContainerNo가 있는 경우에만 추가 
      const hcIndex = oih.container.findIndex(x => x.mCntId === omc.id);

      if (hcIndex === -1) {
        const hc = new OhContainer();
        hc.mCntId = omc.id;
        hc.pkgs = omc.pkgs;
        hc.agentShow = false;
        hc.containerNo = omc.containerNo;
        hc.conType = omc.conType;
        hc.sealNo = omc.sealNo;
        hc.mPkgs = omc.pkgs;
        hc.mUnit = omc.pUnit;
        oih.container.splice(i, 0, hc);
      } else {
        oih.container[hcIndex].containerNo = omc.containerNo;
        oih.container[hcIndex].conType = omc.conType;
        oih.container[hcIndex].sealNo = omc.sealNo;
        oih.container[hcIndex].mPkgs = omc.pkgs;
        oih.container[hcIndex].mUnit = omc.pUnit;
      }
    }
  }

  return oih;
}